import { toast } from "sonner";

import { queryClient } from "../../App";
import { toJobPreferencesRequestBody } from "../handlers/jobPreferences";
import { userAccountKey, useUpdateJobPreferences, useUserAccount } from "../services/create-resume/queries";

/**
 * Read and write the current job-search status.
 *
 * There is exactly one copy of it — the account cache entry — and the nav badge, the profile card
 * and the preferences modal all render from that entry, so changing it anywhere moves all three.
 * The write is applied to the cache before the request goes out, otherwise the badge would sit on
 * the old status for a whole round trip after the user picked a new one; `useUpdateJobPreferences`
 * invalidates afterwards, so the server still has the last word.
 */
export function useJobSearchStatus() {
	const { data: account } = useUserAccount();
	const { mutate, isPending } = useUpdateJobPreferences();

	const status: string = account?.jobPreferences?.currentJobsearchStatus || "";

	const setStatus = (next: string) => {
		if (!account || next === status) return;

		const previous = queryClient.getQueryData(userAccountKey);

		queryClient.setQueryData(userAccountKey, (current: any) =>
			current ? { ...current, jobPreferences: { ...current.jobPreferences, currentJobsearchStatus: next } } : current,
		);

		// The endpoint replaces the whole preferences object, so the other fields have to ride along
		// or picking a status would wipe them.
		mutate(
			toJobPreferencesRequestBody(
				{ ...account.jobPreferences, currentJobsearchStatus: next },
				{
					emailAddress: account.email,
					firstName: account.firstName,
					middleName: account.middleName,
					lastName: account.lastName,
					phoneNumber: account.phoneNumber,
				},
			),
			{
				onError: (error: any) => {
					queryClient.setQueryData(userAccountKey, previous);
					const message = error?.response?.data?.message;
					toast.error(
						(Array.isArray(message) ? message[0] : message) || "Could not update your job status. Please try again.",
					);
				},
			},
		);
	};

	return { status, setStatus, isSaving: isPending };
}
